import { env } from "~/env";

export const DEFAULT_EVENT_IMAGE = "/assets/wellmate-me-leaf-bg.png";

// Category → default event cover, used when a facilitator creates an event
// without uploading an image. Keyed by the lowercased category name. The art is
// the Path Collective brand icon set (full-bleed gradient cards) under
// /public/assets/category-defaults. Mapping of category → brand icon:
//   connect   → Relationships   growth → Contribution   explore → Skills
//   wellbeing → Health          create → Creation
export const CATEGORY_DEFAULT_EVENT_IMAGES: Record<string, string> = {
  connect: "/assets/category-defaults/connect.png",
  growth: "/assets/category-defaults/growth.png",
  explore: "/assets/category-defaults/explore.png",
  wellbeing: "/assets/category-defaults/wellbeing.png",
  create: "/assets/category-defaults/create.png",
};

// An event's categories arrive as `{ name }[]` (API) or plain strings depending
// on the surface; accept either.
type EventCategory = string | { name?: string | null } | null | undefined;

// Pick the default cover for an event based on its category. When an event has
// multiple categories the first one with a mapping wins (array order = the
// event's primary category). Falls back to the generic leaf default.
export function defaultEventImage(categories?: EventCategory[] | null): string {
  if (!categories?.length) return DEFAULT_EVENT_IMAGE;
  for (const c of categories) {
    const name = (typeof c === "string" ? c : c?.name)?.trim().toLowerCase();
    if (name && CATEGORY_DEFAULT_EVENT_IMAGES[name]) {
      return CATEGORY_DEFAULT_EVENT_IMAGES[name];
    }
  }
  return DEFAULT_EVENT_IMAGE;
}

export function resolveImageUrl(imageOrKey: string | null | undefined): string {
  if (!imageOrKey) return DEFAULT_EVENT_IMAGE;
  if (/^https?:\/\//i.test(imageOrKey)) return imageOrKey;
  if (imageOrKey.startsWith('/')) return imageOrKey;
  const base = env.NEXT_PUBLIC_IMAGE_BASE_URL;
  if (!base) return imageOrKey;
  const normalizedBase = base.replace(/\/$/, "");
  const normalizedKey = imageOrKey.replace(/^\//, "");
  return `${normalizedBase}/${normalizedKey}`;
}

// #1648/#1664 — `event.image_url` may be a comma-separated list of multiple
// image keys/URLs (see `coverImages` parsing in `/events/[slug]/page.tsx`).
// Every resolver that accepts a raw `image_url` must take only the first
// entry before resolving — one helper, so the rule can't drift between call
// sites the way it did when #1648 only fixed resolveAbsoluteImageUrl and left
// resolveEventImage (used at ~23 call sites: bookings, checkout, hosting
// cards) still handing the whole string to resolveImageUrl.
//
// Returns the original value unchanged when there is no comma (the common
// case), and the first non-empty trimmed entry when there is — `undefined`
// if every entry is empty (a bare "," or ", ,").
function firstImageEntry(
  imageOrKey: string | null | undefined,
): string | null | undefined {
  if (!imageOrKey?.includes(',')) return imageOrKey;
  return imageOrKey.split(',').map((img) => img.trim()).filter(Boolean)[0];
}

// #1624 — og:image (and the Twitter card equivalent) must be a fully
// qualified, publicly fetchable URL: a crawler has no page origin to
// resolve a relative path against. resolveImageUrl() leaves a path that
// already starts with "/" alone — correct for an <img src>, which the
// browser resolves against the current page, but exactly the case that
// silently broke every default (no-upload) event cover here, since every
// DEFAULT_EVENT_IMAGE / CATEGORY_DEFAULT_EVENT_IMAGES entry is one of those
// paths. Resolve through resolveImageUrl first (handles a bare DO Spaces
// key), then qualify anything still relative against the web app's own
// origin — the same fallback used for openGraph.url.
//
// #1648: `event.image_url` may be a comma-separated list of multiple image
// keys/URLs (see `coverImages` parsing in `/events/[slug]/page.tsx`), which
// neither this function nor resolveImageUrl() used to split on; an og:image
// for a multi-image event came out as one malformed, concatenated URL. Take
// only the first entry, same convention `coverImages` already uses.
export function resolveAbsoluteImageUrl(
  imageOrKey: string | null | undefined,
  webBaseUrl: string,
): string {
  const resolved = resolveImageUrl(firstImageEntry(imageOrKey));
  if (/^https?:\/\//i.test(resolved)) return resolved;
  const base = webBaseUrl.replace(/\/$/, "");
  const path = resolved.startsWith('/') ? resolved : `/${resolved}`;
  return `${base}${path}`;
}

// Like resolveImageUrl, but when the event has no image the fallback is chosen
// from the event's category (see defaultEventImage) instead of the generic leaf.
// Use this for EVENT images (not avatars). `imageOrKey` may be a comma-separated
// list of images (#1664: takes the first, via firstImageEntry — a bare "," or
// an all-empty list falls through to the category default same as no image).
export function resolveEventImage(
  imageOrKey: string | null | undefined,
  categories?: EventCategory[] | null,
): string {
  const firstImage = firstImageEntry(imageOrKey);
  if (firstImage && firstImage.trim()) return resolveImageUrl(firstImage);
  return defaultEventImage(categories);
}


